import React from 'react';
import { isVypisEgsbSubmission } from '@gov-nx/api';
import { DownloadAttachmentListener } from '@gov-nx/component/common';
import { useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useMojePodaniDetailContextInstance } from '@gov-nx/module/page';
import { Button, ContentLayout, InfoBanner } from '@gov-nx/ui/web';
import { toDateTimeReadable } from '@gov-nx/utils/common';
import { CmsPageRender } from '../../../Cms/CmsPageRender';
import { EGSBSubmission } from './EGSBSubmission';
import { MySubmissionMessageList } from './MySubmissionMessageList';
import { MySubmissionsDetailSkeleton } from './MySubmissionsDetailSkeleton';

export const Item = ({ date, label }: { label: string; date: string }) => (
	<div className="flex items-center">
		<dt className="mr-1!">{label}</dt>
		<dd>
			<strong>{toDateTimeReadable(date)}</strong>
		</dd>
	</div>
);

export interface MySubmissionDetailProps {
	code: PageCode;
}

export const MySubmissionDetail = ({ code }: MySubmissionDetailProps) => {
	const { submission, controls, messages, documents, strings, keyValues } =
		useMojePodaniDetailContextInstance();
	const { t } = useLocale(code);

	return (
		<CmsPageRender
			code={code}
			showDescription={false}
			override={{
				name: strings.submission.name,
			}}>
			{controls.initialLoading ? (
				<MySubmissionsDetailSkeleton />
			) : (
				<>
					<DownloadAttachmentListener />
					<dl>
						{keyValues.map((item) => (
							<Item
								key={item.label}
								label={item.label}
								date={item.date}
							/>
						))}
						<dt>
							{submission?.automatickeZpracovani
								? `${t('moje-podani.automaticke-zpracovani')}`
								: null}
						</dt>
					</dl>

					{documents.map((document) => (
						<Button
							key={document.documentId}
							aria-label={t('akce.wcag.stahnout-vypis')}
							type="solid"
							color="primary"
							size="m"
							isLoading={document.isDownloading}
							isDisabled={document.isDownloading}
							onClick={document.onDownload}>
							{t('akce.stahnout-vypis')}
						</Button>
					))}

					{submission && isVypisEgsbSubmission(submission) && (
						<EGSBSubmission submission={submission} />
					)}

					<h2 className="text-body-l-bold!">
						{t('moje-podani.komunikace-v-ramci-podani')}
					</h2>
					{messages.length && submission ? (
						<MySubmissionMessageList code={code} />
					) : (
						<ContentLayout>
							<InfoBanner
								headline={t('stav.chyba.zadne-datove-zpravy')}
								text={t('stav.chyba.nenasli-jsme-zadne-datove-zpravy')}
							/>
						</ContentLayout>
					)}
				</>
			)}
		</CmsPageRender>
	);
};
